/**
 * The one visibility-rating ladder.
 *
 * Five copies of this ladder existed and three of them agreed. The annual heatmap
 * legend, BestNights and the PDF/Excel exports used 8/6/4/2; CalendarView used
 * 9/7/5/3, so a night scoring 8 read "Very Good" on the calendar while sitting in
 * the legend's "Excellent" band; and shootingQualityScore had no "Very Good" at all
 * and started "Good" at 6. A published blog post described the heatmap as three
 * colours with Fair spanning 2-5.
 *
 * The legend is what a reader actually sees next to the colours, so its boundaries
 * win. Everything imports from here. Adding a sixth copy is the failure mode this
 * module exists to prevent.
 */

export type BandName =
  | 'Excellent'
  | 'Very Good'
  | 'Good'
  | 'Fair'
  | 'Poor'
  | 'Not Visible';

export interface Band {
  /** Lowest rating that earns this label. */
  min: number;
  name: BandName;
  /** Inclusive range as the legend prints it. */
  range: string;
}

/** Ordered high to low. The first band whose `min` a rating meets wins. */
export const VISIBILITY_BANDS: readonly Band[] = [
  { min: 8, name: 'Excellent', range: '8-10' },
  { min: 6, name: 'Very Good', range: '6-7' },
  { min: 4, name: 'Good', range: '4-5' },
  { min: 2, name: 'Fair', range: '2-3' },
  { min: 1, name: 'Poor', range: '1' },
  { min: 0, name: 'Not Visible', range: '0' },
] as const;

/** The label for a 0-10 visibility rating. */
export function visibilityBand(rating: number): BandName {
  const r = Number.isFinite(rating) ? rating : 0;
  return (VISIBILITY_BANDS.find((b) => r >= b.min) ?? VISIBILITY_BANDS[VISIBILITY_BANDS.length - 1]).name;
}

/**
 * A night worth planning around.
 *
 * Counters disagreed too: four places counted rating >= 4 and TripStopCard used
 * >= 5, so the same trip reported a different number of good nights depending on
 * which card you read. 4 is the bottom of "Good", which is what the word means.
 */
export const GOOD_NIGHT_MIN = 4;

export function isGoodNight(rating: number): boolean {
  return Number.isFinite(rating) && rating >= GOOD_NIGHT_MIN;
}
